import { useEffect, useMemo, useState } from "react";
import API from "../../services";
import Loader from "../../components/Loader";
import ErrorMessage from "../../components/ErrorMessage";
import Empty from "../../components/Empty";
import { Colors } from "../../utils/colors";
import { Fonts } from "../../utils/fonts";
import type { IContactsListItem } from "../../services/types";

import closeIcon from "../../assets/closeIcon.png";
import { FontSizes } from "../../assets/fonts/fontSizes";
// import "./ContactListing.css";

type Props = {
  title: string;
  contactCategoryGuid: string;
};

const generateInitials = (fullName: string) => {
  const names = fullName?.trim().split(/\s+/);
  return names
    ?.map((n) => n[0] || "")
    .join("")
    .substring(0, 2)
    .toUpperCase();
};

function Initials({ fullName }: { fullName: string }) {
  return (
    <div style={styles.initialsContainer}>
      <div className="contact-initials" style={styles.initials}>
        {generateInitials(fullName)}
      </div>
    </div>
  );
}

export default function ContactListing({ title, contactCategoryGuid }: Props) {
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState<IContactsListItem | null>(null);

  const [isLoading, setLoading] = useState(true);
  const [contacts, setContacts] = useState<IContactsListItem[]>([]);
  const [error, setError] = useState(false);

  // Reload contacts whenever tab id changes
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(false);

        const response = await API.getContacts(contactCategoryGuid);
        if (!Array.isArray(response?.data)) throw new Error("Bad contacts payload");

        // Optional: sort by sortOrder (RN API provides it)
        const sorted = [...response?.data].sort(
          (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
        );

        setContacts(sorted);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [contactCategoryGuid]);

  console.log(contacts,'contacts with the guid');
  

  const subtitle = useMemo(() => {
    const t = title?.toLowerCase();
    return `To view the ${t}'s name, contact details, and other details, click below.`;
  }, [title]);

  const onContactPress = (item: IContactsListItem) => {
    setSelected(item);
    setModalOpen(true);
  };

  const onClose = () => {
    setModalOpen(false);
    setSelected(null);
  };

  if (isLoading) return <Loader />;
  if (error) return <ErrorMessage />;
  if (contacts.length === 0) return <Empty />;

  return (
    <div style={styles.scene}>
      <div className="contact-title" style={styles.title}>
        {title}
      </div>
      <div className="contact-subtitle" style={styles.subTitle}>
        {subtitle}
      </div>

      {/* Grid (3 columns like numColumns={3}) */}
      <div className="contact-grid" style={styles.grid}>
        {contacts?.map((c) => (
          <button
            key={c.contactGuid}
            type="button"
            style={styles.card}
            onClick={() => onContactPress(c)}
          >
            {c.profileImageUrl ? (
              <img
                src={c.profileImageUrl}
                alt={c.name}
                style={styles.profileIcon}
              />
            ) : (
              <Initials fullName={c.name} />
            )}
            <div className="contact-name" style={styles.name}>
              {c.name}
            </div>
            <div className="contact-role" style={styles.role}>
              {c.designation}
            </div>
          </button>
        ))}
      </div>

      {/* Modal */}
      {modalOpen && selected ? (
        <>
          <div style={styles.modalOverlay} onClick={onClose} />
          <div style={styles.modal}>
            <button type="button" onClick={onClose} style={styles.closeButton}>
              <img src={closeIcon} alt="Close" style={styles.closeIcon} />
            </button>

            <div className="contact-modal-text" style={styles.modalText}>
              Scan QR to add as a contact
            </div>

            {selected.qrCodeImageUrl ? (
              <img
                src={selected.qrCodeImageUrl}
                alt="QR"
                style={styles.qrcode}
              />
            ) : null}

            <div style={styles.profile}>
              {selected.profileImageUrl ? (
                <img
                  src={selected.profileImageUrl}
                  alt={selected.name}
                  style={styles.profileIcon}
                />
              ) : (
                <Initials fullName={selected.name} />
              )}

              <div
                className="contact-selected-name"
                style={styles.selectedName}
              >
                {selected.name}
              </div>
              <div
                className="contact-selected-role"
                style={styles.selectedRole}
              >
                {selected.designation}
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  scene: { width: "100%" },

  title: {
    fontSize: FontSizes.hero,
    textAlign: "center",
    textTransform: "capitalize",
    color: Colors.White,
    fontWeight: Fonts.SemiBold,
    padding: "10px 0",
    fontFamily: "Inter",
  },
  subTitle: {
    fontSize: FontSizes.xs,
    textAlign: "center",
    color: Colors.White,
    fontWeight: Fonts.Light,
    paddingBottom: 10,
    fontFamily: "Inter",
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 20,
    padding: 20,
  },

  card: {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },

  profileIcon: { height: 78, width: 78, borderRadius: 39, objectFit: "cover" },

  name: {
    fontSize: FontSizes.lg,
    textAlign: "center",
    color: Colors.White,
    fontWeight: Fonts.Bold,
    paddingTop: 10,
    fontFamily: "Inter",
  },
  role: {
    fontSize: FontSizes.xs,
    textAlign: "center",
    color: Colors.White,
    fontWeight: Fonts.Light,
    paddingTop: 3,
    fontFamily: "Inter",
  },

  initialsContainer: {
    height: 78,
    width: 78,
    borderRadius: 39,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: Colors.SecondaryColor,
  },
  initials: {
    fontSize: FontSizes.xxxxl,
    fontWeight: Fonts.Bold,
    color: Colors.Black,
    fontFamily: "Inter",
  },

  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.30)", // #0000004D-ish
    zIndex: 9997,
  },
  modal: {
    position: "fixed",
    left: "50%",
    bottom: 0,
    transform: "translateX(-50%)",
    height: "80%",
    width: "60%",
    backgroundColor: Colors.White,
    borderTopLeftRadius: 40,
    borderTopRightRadius: 40,
    padding: 20,
    zIndex: 9998,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  closeButton: {
    height: 50,
    width: 50,
    borderRadius: 25,
    backgroundColor: Colors.White,
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "absolute",
    top: -70,
  },
  closeIcon: { height: 24, width: 24, objectFit: "contain" },

  modalText: {
    fontSize: FontSizes.xs,
    fontWeight: Fonts.Light,
    color: Colors.Black,
    paddingBottom: 10,
    marginTop: 20,
    fontFamily: "Inter",
  },

  qrcode: {
    height: "50%",
    aspectRatio: "1 / 1",
    objectFit: "contain",
    marginBottom: 20,
  },

  profile: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
  },
  selectedName: {
    fontSize: FontSizes.xxxl,
    textAlign: "center",
    color: Colors.Black,
    fontWeight: Fonts.Bold,
    paddingTop: 10,
    fontFamily: "Inter",
  },
  selectedRole: {
    fontSize: FontSizes.sm,
    textAlign: "center",
    color: Colors.Black,
    fontWeight: Fonts.Light,
    paddingTop: 3,
    fontFamily: "Inter",
  },
};
